import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Send } from 'lucide-react';
import { localizedName } from '@/components/localized-name';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  DEFAULT_OFFLINE_METHOD,
  OFFLINE_PAYMENT_METHODS,
  paymentMethodLabelKey,
} from '@/features/events/payment-methods';
import { formatCurrency } from '@/lib/format';
import { toApiDate } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import { RestrictionPickers } from './restriction-pickers';
import { UserPicker, type PickedUser } from './user-picker';
import {
  NO_RESTRICTIONS,
  toRestrictionsInput,
  validityLabel,
  type RestrictionDraft,
} from './voucher-format';
import { useIssueVoucher, useVoucherProducts } from './vouchers.api';
import { VALIDITY_UNIT, type IssueVoucher, type ValidityUnit } from './types';

/**
 * Create a voucher outside the online checkout: given away (marketing, an
 * apology, a giveaway) or sold at the desk with the money recorded here.
 *
 * Three independent questions, one per section, because each has a different
 * answer for the same situation — *what* (a catalogue voucher or custom terms),
 * *who* (a known account, an email, or a code handed over in person) and *paid
 * how* (free, or an offline payment).
 */

type Terms = 'product' | 'custom';
type Recipient = 'account' | 'email' | 'code';
type Expiry = 'period' | 'date' | 'never';

const CUSTOM_DEFAULTS = {
  nameEn: '',
  nameEs: '',
  descriptionEn: '',
  descriptionEs: '',
  uses: 1,
  validityAmount: 3,
  validityUnit: VALIDITY_UNIT.months as ValidityUnit,
  expiresOn: '',
};

function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

export function IssueVoucherSheet({
  open,
  onOpenChange,
  onIssued,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The new voucher's id, so the caller can open it. */
  onIssued: (voucherId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const products = useVoucherProducts();
  const issue = useIssueVoucher();

  const [terms, setTerms] = useState<Terms>('product');
  const [productId, setProductId] = useState('');
  const [custom, setCustom] = useState(CUSTOM_DEFAULTS);
  const [expiry, setExpiry] = useState<Expiry>('period');
  const [restrictions, setRestrictions] = useState<RestrictionDraft>(NO_RESTRICTIONS);

  const [recipient, setRecipient] = useState<Recipient>('account');
  const [recipientUser, setRecipientUser] = useState<PickedUser | null>(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientLanguage, setRecipientLanguage] = useState('0');
  const [message, setMessage] = useState('');

  const [complimentary, setComplimentary] = useState(true);
  const [buyer, setBuyer] = useState<PickedUser | null>(null);
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState(String(DEFAULT_OFFLINE_METHOD));
  const [reference, setReference] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  // A fresh form every time it opens.
  useEffect(() => {
    if (!open) return;
    setTerms('product');
    setProductId('');
    setCustom(CUSTOM_DEFAULTS);
    setExpiry('period');
    setRestrictions(NO_RESTRICTIONS);
    setRecipient('account');
    setRecipientUser(null);
    setRecipientEmail('');
    setRecipientName('');
    setRecipientLanguage('0');
    setMessage('');
    setComplimentary(true);
    setBuyer(null);
    setAmount(0);
    setMethod(String(DEFAULT_OFFLINE_METHOD));
    setReference('');
    setAdminNotes('');
  }, [open]);

  // Admins can issue from products that are off sale too; deleted ones are gone.
  const productOptions = useMemo(() => products.data ?? [], [products.data]);
  const product = productOptions.find((p) => p.id === productId) ?? null;

  // Selling a catalogue voucher at the desk: the price is the obvious amount.
  useEffect(() => {
    if (product) setAmount(product.price);
  }, [product]);

  const setCustomField = (patch: Partial<typeof CUSTOM_DEFAULTS>) =>
    setCustom((current) => ({ ...current, ...patch }));

  const problem =
    terms === 'product' && !product
      ? t('vouchers.issue.errors.product')
      : terms === 'custom' && (!custom.nameEn.trim() || !custom.nameEs.trim())
        ? t('vouchers.errors.nameRequired')
        : terms === 'custom' && custom.uses < 1
          ? t('vouchers.errors.usesInvalid')
          : terms === 'custom' && expiry === 'date' && !custom.expiresOn
            ? t('vouchers.issue.errors.expiry')
            : terms === 'custom' && expiry === 'period' && custom.validityAmount < 1
              ? t('vouchers.errors.validityInvalid')
              : recipient === 'account' && !recipientUser
                ? t('vouchers.issue.errors.account')
                : recipient === 'email' && !recipientEmail.trim()
                  ? t('vouchers.issue.errors.email')
                  : !complimentary && amount <= 0
                    ? t('vouchers.issue.errors.amount')
                    : null;

  function submit() {
    if (problem) {
      toast.error(problem);
      return;
    }

    const body: IssueVoucher = {
      voucherProductId: terms === 'product' ? productId : null,
      recipientUserId: recipient === 'account' ? recipientUser!.id : null,
      recipientEmail: recipient === 'email' ? recipientEmail.trim() : null,
      recipientName:
        recipient === 'email' ? recipientName.trim() || null : null,
      recipientLanguage: Number(recipientLanguage),
      message: message.trim() || null,
      isComplimentary: complimentary,
      buyerUserId: complimentary ? null : (buyer?.id ?? null),
      amount: complimentary ? null : amount,
      paymentMethod: complimentary ? null : Number(method),
      offlineReference: complimentary ? null : reference.trim() || null,
      receivedAt: null,
      adminNotes: adminNotes.trim() || null,
    };

    if (terms === 'custom') {
      Object.assign(body, {
        nameEn: custom.nameEn.trim(),
        nameEs: custom.nameEs.trim(),
        descriptionEn: custom.descriptionEn.trim() || null,
        descriptionEs: custom.descriptionEs.trim() || null,
        uses: custom.uses,
        noExpiry: expiry === 'never',
        expiresAt: expiry === 'date' ? toApiDate(custom.expiresOn, true) : null,
        validityAmount: expiry === 'period' ? custom.validityAmount : undefined,
        validityUnit: expiry === 'period' ? custom.validityUnit : undefined,
        restrictions: toRestrictionsInput(restrictions),
      } satisfies Partial<IssueVoucher>);
    }

    issue.mutate(body, {
      onSuccess: (voucher) => {
        toast.success(t('vouchers.issue.done'));
        onOpenChange(false);
        onIssued(voucher.id);
      },
      onError: (error) =>
        toast.error(
          error instanceof Error ? error.message : t('vouchers.issue.failed')
        ),
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b">
          <SheetTitle>{t('vouchers.issue.title')}</SheetTitle>
        </SheetHeader>

        <div className="space-y-6 p-4">
          {/* ---------------------------------------------------- what */}
          <Section title={t('vouchers.issue.what')}>
            <Tabs value={terms} onValueChange={(v) => setTerms(v as Terms)}>
              <TabsList className="w-full">
                <TabsTrigger value="product" className="flex-1">
                  {t('vouchers.issue.fromCatalogue')}
                </TabsTrigger>
                <TabsTrigger value="custom" className="flex-1">
                  {t('vouchers.issue.custom')}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {terms === 'product' ? (
              <Field label={t('vouchers.voucher')}>
                <Select value={productId} onValueChange={setProductId}>
                  <SelectTrigger disabled={products.isPending}>
                    <SelectValue
                      placeholder={
                        products.isPending
                          ? t('common.loading')
                          : t('vouchers.issue.pickProduct')
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {productOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {localizedName(p, language)} · {formatCurrency(p.price)}
                        {!p.isActive && ` (${t('vouchers.offSale')})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {product && (
                  <p className="text-muted-foreground text-xs">
                    {t('vouchers.usesCount', { count: product.uses })} ·{' '}
                    {validityLabel(product.validityAmount, product.validityUnit, t)}
                  </p>
                )}
              </Field>
            ) : (
              <div className="space-y-4">
                <p className="text-muted-foreground text-xs">
                  {t('vouchers.issue.customHint')}
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={`${t('vouchers.name')} (EN)`}>
                    <Input
                      value={custom.nameEn}
                      maxLength={200}
                      onChange={(e) => setCustomField({ nameEn: e.target.value })}
                    />
                  </Field>
                  <Field label={`${t('vouchers.name')} (ES)`}>
                    <Input
                      value={custom.nameEs}
                      maxLength={200}
                      onChange={(e) => setCustomField({ nameEs: e.target.value })}
                    />
                  </Field>
                </div>
                <Field label={`${t('vouchers.description')} (EN)`}>
                  <Textarea
                    rows={2}
                    maxLength={1000}
                    value={custom.descriptionEn}
                    onChange={(e) =>
                      setCustomField({ descriptionEn: e.target.value })
                    }
                  />
                </Field>
                <Field label={`${t('vouchers.description')} (ES)`}>
                  <Textarea
                    rows={2}
                    maxLength={1000}
                    value={custom.descriptionEs}
                    onChange={(e) =>
                      setCustomField({ descriptionEs: e.target.value })
                    }
                  />
                </Field>
                <Field label={t('vouchers.uses')} hint={t('vouchers.usesHint')}>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={custom.uses}
                    onChange={(e) => setCustomField({ uses: Number(e.target.value) })}
                  />
                </Field>

                <Field label={t('vouchers.expires')}>
                  <Select value={expiry} onValueChange={(v) => setExpiry(v as Expiry)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="period">
                        {t('vouchers.issue.expiryPeriod')}
                      </SelectItem>
                      <SelectItem value="date">
                        {t('vouchers.issue.expiryDate')}
                      </SelectItem>
                      <SelectItem value="never">{t('vouchers.never')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {expiry === 'period' && (
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        value={custom.validityAmount}
                        onChange={(e) =>
                          setCustomField({ validityAmount: Number(e.target.value) })
                        }
                      />
                      <Select
                        value={String(custom.validityUnit)}
                        onValueChange={(v) =>
                          setCustomField({ validityUnit: Number(v) as ValidityUnit })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={String(VALIDITY_UNIT.days)}>
                            {t('vouchers.days')}
                          </SelectItem>
                          <SelectItem value={String(VALIDITY_UNIT.months)}>
                            {t('vouchers.months')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {expiry === 'date' && (
                    <Input
                      type="date"
                      value={custom.expiresOn}
                      onChange={(e) => setCustomField({ expiresOn: e.target.value })}
                    />
                  )}
                </Field>

                <RestrictionPickers
                  value={restrictions}
                  onChange={setRestrictions}
                  enabled={open}
                />
              </div>
            )}
          </Section>

          {/* ----------------------------------------------------- who */}
          <Section title={t('vouchers.issue.who')}>
            <Tabs
              value={recipient}
              onValueChange={(v) => setRecipient(v as Recipient)}
            >
              <TabsList className="w-full">
                <TabsTrigger value="account" className="flex-1">
                  {t('vouchers.issue.toAccount')}
                </TabsTrigger>
                <TabsTrigger value="email" className="flex-1">
                  {t('vouchers.issue.toEmail')}
                </TabsTrigger>
                <TabsTrigger value="code" className="flex-1">
                  {t('vouchers.issue.codeOnly')}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {recipient === 'account' && (
              <UserPicker
                value={recipientUser}
                onChange={setRecipientUser}
                enabled={open}
              />
            )}

            {recipient === 'email' && (
              <>
                <Field
                  label={t('vouchers.recipientEmail')}
                  hint={t('vouchers.issue.emailHint')}
                >
                  <Input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                  />
                </Field>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t('vouchers.recipientName')}>
                    <Input
                      value={recipientName}
                      maxLength={200}
                      onChange={(e) => setRecipientName(e.target.value)}
                    />
                  </Field>
                  <Field label={t('vouchers.emailLanguage')}>
                    <Select
                      value={recipientLanguage}
                      onValueChange={setRecipientLanguage}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">English</SelectItem>
                        <SelectItem value="1">Español</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </>
            )}

            {recipient === 'code' && (
              <p className="text-muted-foreground text-xs">
                {t('vouchers.issue.codeOnlyHint')}
              </p>
            )}

            {recipient !== 'code' && (
              <Field label={t('vouchers.message')}>
                <Textarea
                  rows={2}
                  maxLength={1000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('vouchers.issue.messagePlaceholder')}
                />
              </Field>
            )}
          </Section>

          {/* ---------------------------------------------------- paid */}
          <Section title={t('vouchers.issue.payment')}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="text-xs font-semibold">
                  {t('vouchers.complimentary')}
                </Label>
                <p className="text-muted-foreground text-xs">
                  {t('vouchers.issue.complimentaryHint')}
                </p>
              </div>
              <Switch checked={complimentary} onCheckedChange={setComplimentary} />
            </div>

            {!complimentary && (
              <div className="space-y-4 rounded-lg border p-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t('vouchers.issue.amount')}>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value))}
                    />
                  </Field>
                  <Field label={t('vouchers.issue.method')}>
                    <Select value={method} onValueChange={setMethod}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OFFLINE_PAYMENT_METHODS.map((value) => (
                          <SelectItem key={value} value={String(value)}>
                            {t(paymentMethodLabelKey(value))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label={t('vouchers.issue.reference')}>
                  <Input
                    value={reference}
                    maxLength={200}
                    onChange={(e) => setReference(e.target.value)}
                  />
                </Field>
                <Field
                  label={t('vouchers.issue.buyer')}
                  hint={t('vouchers.issue.buyerHint')}
                >
                  <UserPicker value={buyer} onChange={setBuyer} enabled={open} />
                </Field>
              </div>
            )}
          </Section>

          <Field label={t('vouchers.adminNotes')}>
            <Textarea
              rows={2}
              maxLength={1000}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
            />
          </Field>

          <Button className="w-full" disabled={issue.isPending} onClick={submit}>
            {issue.isPending ? <Loader2 className="animate-spin" /> : <Send />}
            {t('vouchers.issue.submit')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
